/**
 * API Client Configuration
 * Base fetch wrapper with error handling and cookie-based auth
 *
 * With Better Auth, sessions are managed via HTTP-only cookies.
 * No token passing needed - cookies are sent automatically with credentials: 'include'.
 */

const API_BASE_URL = "/api/backend";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Base fetch wrapper with cookie-based auth and error handling
 *
 * Usage:
 *   const data = await apiFetch('/recipes');
 *   const recipe = await apiFetch('/recipes/123');
 *   await apiFetch('/recipes/favorites/123', { method: 'POST' });
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      // Send session cookies automatically (Better Auth)
      credentials: "include",
    });

    // Handle non-JSON responses (like 204 No Content)
    if (response.status === 204) {
      return undefined as T;
    }

    // Check content type to ensure it's JSON
    const contentType = response.headers.get("content-type");
    const isJson = contentType?.includes("application/json");

    if (!isJson) {
      // Non-JSON response (likely an error page)
      const text = await response.text();
      throw new ApiError(
        `API returned non-JSON response: ${response.statusText}`,
        response.status,
        text,
      );
    }

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.message ||
          data.error ||
          `API request failed: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or parsing error
    throw new ApiError(
      error instanceof Error ? error.message : "Unknown error occurred",
      0,
    );
  }
}

/**
 * Fetch wrapper for endpoints that return plain text (e.g. grocery list
 * export). apiFetch rejects non-JSON bodies by design, so text responses need
 * their own path.
 */
export async function apiFetchText(
  endpoint: string,
  options: RequestInit = {},
): Promise<string> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type");

    if (contentType?.includes("application/json")) {
      const data = await response.json().catch(() => undefined);
      throw new ApiError(
        data?.message ||
          data?.error ||
          `API request failed: ${response.statusText}`,
        response.status,
        data,
      );
    }

    throw new ApiError(
      `API request failed: ${response.statusText}`,
      response.status,
      await response.text().catch(() => undefined),
    );
  }

  return response.text();
}
